import React, { useEffect, useState, useMemo } from 'react'
import { Trans } from '@lingui/react/macro'

import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'

import { loadStripe } from '@stripe/stripe-js'
import type { PaymentIntent, Stripe } from '@stripe/stripe-js'
import {
  STRIPE_PUBLISHABLE_KEY,
  stripePromise as guildStripePromise,
} from '@data/stripe'

import { HOST_NAME } from '@data/config/url'

import { Button, Form, Paragraph, Anchor } from '@newComponents'
import { Spinner } from '@newComponents/Spinner'

import { XStack, useTheme, getTokens } from 'tamagui'

import InterVariableFont from '../../../assets/Inter-Font/InterVariable.woff2'

import { SCALE_RATIO, LINE_HEIGHT_RATIO } from '@data/config/fontScale'

export const GuildDonationPaymentForm = ({
  clientSecret,
  backToAmountForm,
  guildSlugId,
  stripeAccountId,
}: {
  clientSecret: string
  backToAmountForm: () => any
  guildSlugId: string
  stripeAccountId?: string | null
}) => {
  const theme = useTheme()

  const [stripePromise] = useState(() =>
    stripeAccountId
      ? loadStripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: stripeAccountId })
      : guildStripePromise
  )

  return (
    <>
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            variables: {
              fontFamily: 'Inter',
              fontSmooth: 'always',
              fontSizeBase: '16px',
              fontSizeSm: `${16 / SCALE_RATIO}px`,
              fontLineHeight: LINE_HEIGHT_RATIO.toString(),
              spacingUnit: '4px',
              borderRadius: '8px',
              colorPrimary: theme.primaryColor.val,
              colorBackground: theme.background.val,
              colorText: theme.color.val,
              colorTextPlaceholder: '#9A97A8',
              colorTextSecondary: getTokens().color.grey7.val,
              colorSuccess: theme.successColor.val,
              colorDanger: theme.errorColor.val,
            },
            rules: {
              '.Input': {
                borderColor: theme.borderColor.val,
              },
              '.Input:hover': {
                borderColor: theme.borderColorHover.val,
              },
            },
          },
          fonts: [{ family: 'Inter', src: `url(${InterVariableFont})` }],
        }}
      >
        <CheckoutForm
          clientSecret={clientSecret}
          backToAmountForm={backToAmountForm}
          guildSlugId={guildSlugId}
          stripePromise={stripePromise}
        />
      </Elements>
    </>
  )
}

const CheckoutForm = ({
  clientSecret,
  backToAmountForm,
  guildSlugId,
  stripePromise,
}: {
  clientSecret: string
  backToAmountForm: () => any
  guildSlugId: string
  stripePromise: Promise<Stripe | null>
}) => {
  const stripe = useStripe()
  const elements = useElements()

  const [paymentIntent, setPaymentIntent] = useState<
    PaymentIntent | undefined
  >()

  useEffect(() => {
    void (async () => {
      const stripe = await stripePromise

      if (stripe) {
        const result = await stripe.retrievePaymentIntent(clientSecret)

        if (result.error) {
          console.error('ERROR WHILE RETRIEVING PAYMENT INTENT', result.error)
        }

        setPaymentIntent(result.paymentIntent)
      }
    })()
  }, [location, clientSecret])

  const donationAmount = useMemo(() => {
    if (!paymentIntent) {
      return
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: paymentIntent.currency,
      maximumFractionDigits: 0,
    }).format(paymentIntent.amount / 100)
  }, [paymentIntent])

  if (!stripe || !elements || !paymentIntent) {
    return <Spinner size='$9' color='$primaryColor' />
  }

  return (
    <>
      <Form
        initialValues={{}}
        onSubmit={async (values, actions) => {
          await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: `${HOST_NAME}/${guildSlugId}/donate/confirm`,
            },
          })
        }}
      >
        <PaymentElement />

        <XStack justifyContent='flex-end'>
          <XStack flex={1} alignItems='center'>
            <Anchor
              size='$4'
              onPress={() => {
                backToAmountForm()
              }}
            >
              <Trans>← Change Donation Amount</Trans>
            </Anchor>
          </XStack>

          <Form.SubmitButton>
            <Form.SubmitButton.Text>
              <Trans>Donate {donationAmount}</Trans>
            </Form.SubmitButton.Text>
          </Form.SubmitButton>
        </XStack>
      </Form>

      <Paragraph size='$4' color='$colorSubtle' textAlign='right'>
        <Trans>Donations are non-refundable</Trans>
      </Paragraph>
    </>
  )
}
